import React, { useEffect, useMemo, useState } from 'react';
import { View, ScrollView, Text, Pressable, Platform, ViewStyle } from 'react-native';
import { Button, IconButton, ActivityIndicator, Portal, Modal, Checkbox } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchClients } from '../redux/slices/clientsSlice';
import { fetchProduits } from '../redux/slices/produitsSlice';
import { createCommande, updateCommande, fetchCommandeById } from '../redux/slices/commandesSlice';
import { produitServiceService } from '../services/produitService';
import { vehiculeService } from '../services/vehiculeService';
import { RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { Avatar, Badge, BadgeTone, Card, SearchPill, SectionHeader, Screen, TextInput } from '../components/ui';
import EmptyState from '../components/EmptyState';
import { RootStackParamList } from '../navigation/AppNavigator';
import { ProduitService, StatutCommande, Vehicule, getServiceDisplayName } from '../types';

type RouteType = RouteProp<RootStackParamList, 'NouvelleCommande'>;

/** Status pill shown in the summary card (Devis → warning, Bon de commande → brand, Facture/Payée → success). */
const STATUT_BADGES: Record<StatutCommande, { label: string; tone: BadgeTone }> = {
  DEVIS: { label: 'Devis', tone: 'warning' },
  BON_COMMANDE: { label: 'Bon de commande', tone: 'brand' },
  FACTURE: { label: 'Facture', tone: 'success' },
  PAYEE: { label: 'Payée', tone: 'success' },
};

/** Keeps the summary card visible while scrolling the form (web desktop only). */
const STICKY_WEB = { position: 'sticky', top: 24 } as unknown as ViewStyle;

// tempId: local key; produitId null = new product to create on submit
interface LigneItem {
  tempId: number;
  produitId: number | null;
  code: string;
  nom?: string;
  prixUnitaire: number;
  quantite: number;
  detail?: string;
  vehiculeIds: number[];
  vehicleSelectionHydrated: boolean;
}

const ABONNEMENT_CODES = ['*BAS', '*AS', '*A'];

function isAbonnementCode(code: string): boolean {
  return ABONNEMENT_CODES.includes((code || '').toUpperCase());
}

function extractVehicleIdsFromDetail(detail: string | undefined, vehicules: Vehicule[]): number[] {
  if (!detail) return [];
  const vehicleLine = detail
    .split(/\r?\n/)
    .find((line) => /^v[ée]hicules?(?:\s*\(\d+\))?\s*:/i.test(line.trim()));
  if (!vehicleLine) return [];

  const separatorIndex = vehicleLine.indexOf(':');
  if (separatorIndex < 0) return [];
  const immatriculations = vehicleLine
    .slice(separatorIndex + 1)
    .split(',')
    .map((value) => value.trim().toLocaleUpperCase())
    .filter(Boolean);
  return vehicules
    .filter((vehicule) => immatriculations.includes(vehicule.immatriculation.trim().toLocaleUpperCase()))
    .map((vehicule) => vehicule.id);
}

export default function NouvelleCommande() {
  const route = useRoute<RouteType>();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop, isWide } = useLayout();
  const { list: clients } = useAppSelector((s) => s.clients);
  const { list: produits, loading: produitsLoading } = useAppSelector((s) => s.produits);

  const [selectedClientId, setSelectedClientId] = useState<number | null>(route.params?.clientId ?? null);
  const [selectedVehiculeId, setSelectedVehiculeId] = useState<number | null>(route.params?.vehiculeId ?? null);
  const [vehiculeIdsConcernes, setVehiculeIdsConcernes] = useState<number[]>([]);
  const [dureeAbonnementMois, setDureeAbonnementMois] = useState('');
  const [numeroFacture, setNumeroFacture] = useState('');
  const [numeroBonCommande, setNumeroBonCommande] = useState('');
  const [commandeStatut, setCommandeStatut] = useState<StatutCommande | null>(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [lignes, setLignes] = useState<LigneItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientVehicules, setClientVehicules] = useState<Vehicule[]>([]);
  const [loadedVehiculesClientId, setLoadedVehiculesClientId] = useState<number | null>(null);
  const [vehiculesLoading, setVehiculesLoading] = useState(false);
  const [vehiculesError, setVehiculesError] = useState<string | null>(null);
  const [selectingVehiclesTempId, setSelectingVehiclesTempId] = useState<number | null>(null);
  const [vehiculeSearch, setVehiculeSearch] = useState('');

  const editCommandeId = route.params?.commandeId ?? null;

  // Load existing commande for edit mode
  useEffect(() => {
    if (!editCommandeId) return;
    dispatch(fetchCommandeById(editCommandeId)).then((action: any) => {
      const cmd = action.payload;
      if (!cmd) return;
      setDate(cmd.date);
      setCommandeStatut(cmd.statut);
      setNumeroFacture(cmd.numeroFacture ?? (cmd.statut === 'FACTURE' ? `FAC${String(cmd.id).padStart(8, '0')}` : ''));
      setNumeroBonCommande(cmd.numeroBonCommande ?? (cmd.statut === 'BON_COMMANDE' ? `BC${String(cmd.id).padStart(8, '0')}` : ''));
      setSelectedClientId(cmd.client?.id ?? null);
      setSelectedVehiculeId(cmd.vehicule?.id ?? null);
      setVehiculeIdsConcernes((cmd.vehiculesConcernes ?? []).map((v: any) => v.id));
      setDureeAbonnementMois(cmd.dureeAbonnementMois ? String(cmd.dureeAbonnementMois) : '');
      setLignes(
        cmd.lignesCommande.map((l: any) => ({
          tempId: l.id,
          produitId: l.produitService?.id ?? null,
          code: l.produitService?.code ?? '',
          nom: l.produitService?.nom,
          prixUnitaire: Number(l.prixUnitaire),
          quantite: l.quantite,
          detail: l.detail,
          vehiculeIds: [],
          vehicleSelectionHydrated: false,
        }))
      );
    });
  }, [editCommandeId]);

  // Unit price edit dialog state
  const [editingPrixTempId, setEditingPrixTempId] = useState<number | null>(null);
  const [editingPrixValue, setEditingPrixValue] = useState('');

  // New-service (catalogue creation) form state
  const [showNewServiceForm, setShowNewServiceForm] = useState(false);
  const [nsCode, setNsCode] = useState('');
  const [nsNom, setNsNom] = useState('');
  const [nsPrix, setNsPrix] = useState('');
  const [nsError, setNsError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchClients());
    dispatch(fetchProduits());
  }, []);

  useEffect(() => {
    let active = true;
    if (!selectedClientId) {
      setClientVehicules([]);
      setLoadedVehiculesClientId(null);
      setVehiculesError(null);
      setVehiculesLoading(false);
      return () => { active = false; };
    }

    setVehiculesLoading(true);
    setLoadedVehiculesClientId(null);
    setVehiculesError(null);
    vehiculeService.getByClient(selectedClientId)
      .then((vehicules) => {
        if (!active) return;
        setClientVehicules(vehicules);
        setLoadedVehiculesClientId(selectedClientId);
      })
      .catch(() => {
        if (!active) return;
        setClientVehicules([]);
        setVehiculesError('Impossible de charger les véhicules de ce client.');
      })
      .finally(() => {
        if (active) setVehiculesLoading(false);
      });

    return () => { active = false; };
  }, [selectedClientId]);

  const hasUnhydratedVehicleSelections = lignes.some((ligne) => !ligne.vehicleSelectionHydrated);
  useEffect(() => {
    if (!hasUnhydratedVehicleSelections
      || vehiculesLoading
      || loadedVehiculesClientId !== selectedClientId) return;
    setLignes((current) => current.map((ligne) => (
      ligne.vehicleSelectionHydrated
        ? ligne
        : {
          ...ligne,
          vehiculeIds: extractVehicleIdsFromDetail(ligne.detail, clientVehicules),
          vehicleSelectionHydrated: true,
        }
    )));
  }, [
    clientVehicules,
    hasUnhydratedVehicleSelections,
    loadedVehiculesClientId,
    selectedClientId,
    vehiculesLoading,
  ]);

  const selectedClient = clients.find((c) => c.id === selectedClientId);
  const filteredClients = clients.filter((c) =>
    c.nomRaisonSociale.toLowerCase().includes(clientSearch.toLowerCase())
  );
  const filteredVehicules = useMemo(() => {
    const query = vehiculeSearch.trim().toLocaleLowerCase();
    if (!query) return clientVehicules;
    return clientVehicules.filter((vehicule) =>
      vehicule.immatriculation.toLocaleLowerCase().includes(query)
      || vehicule.marque.toLocaleLowerCase().includes(query)
      || vehicule.modele.toLocaleLowerCase().includes(query)
    );
  }, [clientVehicules, vehiculeSearch]);
  const selectingVehicleLine = lignes.find((ligne) => ligne.tempId === selectingVehiclesTempId);
  const contientAbonnement = lignes.some((l) => isAbonnementCode(l.code));
  const produitsCommandeClassique = produits

  // Add item from catalog
  const addFromCatalog = (p: ProduitService) => {
    const existing = lignes.find((l) => l.produitId === p.id);
    if (existing) {
      setLignes(lignes.map((l) => l.produitId === p.id ? { ...l, quantite: l.quantite + 1 } : l));
    } else {
      setLignes([...lignes, {
        tempId: Date.now() + Math.random(),
        produitId: p.id,
        code: p.code,
        nom: p.nom,
        prixUnitaire: Number(p.prixUnitaire),
        quantite: 1,
        detail: p.detail,
        vehiculeIds: [],
        vehicleSelectionHydrated: true,
      }]);
    }
  };

  const removeLigne = (tempId: number) => setLignes(lignes.filter((l) => l.tempId !== tempId));

  const addNewServiceToCommande = () => {
    setNsError(null);
    const codeNorm = nsCode.trim().toUpperCase();
    if (!codeNorm || !codeNorm.startsWith('*') || codeNorm.length < 2) {
      setNsError('Le code doit commencer par * et contenir au moins un caractère (ex: *GPS).');
      return;
    }
    const prix = parseFloat(nsPrix.replace(',', '.'));
    if (isNaN(prix) || prix <= 0) {
      setNsError('Le prix unitaire doit être supérieur à 0.');
      return;
    }
    const existsDansCatalog = produits.some(
      (p) => (p.code ?? '').toUpperCase() === codeNorm,
    );
    if (existsDansCatalog) {
      setNsError(`Le code ${codeNorm} existe déjà dans le catalogue. Utilisez le catalogue ci-dessus.`);
      return;
    }
    setLignes([...lignes, {
      tempId: Date.now() + Math.random(),
      produitId: null,
      code: codeNorm,
      nom: nsNom.trim() || undefined,
      prixUnitaire: prix,
      quantite: 1,
      vehiculeIds: [],
      vehicleSelectionHydrated: true,
    }]);
    setNsCode('');
    setNsNom('');
    setNsPrix('');
    setNsError(null);
    setShowNewServiceForm(false);
  };

  const updateQuantite = (tempId: number, q: number) => {
    if (q <= 0) { removeLigne(tempId); return; }
    setLignes(lignes.map((l) => l.tempId === tempId ? { ...l, quantite: q } : l));
  };

  const clearLineVehicleSelections = () => {
    setLignes((current) => current.map((ligne) => ({
      ...ligne,
      vehiculeIds: [],
      vehicleSelectionHydrated: true,
    })));
  };

  const selectClient = (clientId: number) => {
    clearLineVehicleSelections();
    setSelectedClientId(clientId);
    setSelectedVehiculeId(null);
    setVehiculeIdsConcernes([]);
    setDureeAbonnementMois('');
    setClientSearch('');
  };

  const clearSelectedClient = () => {
    clearLineVehicleSelections();
    setSelectedClientId(null);
    setSelectedVehiculeId(null);
    setVehiculeIdsConcernes([]);
    setDureeAbonnementMois('');
    setClientSearch('');
    setSelectingVehiclesTempId(null);
  };

  const openVehicleSelector = (tempId: number) => {
    setVehiculeSearch('');
    setSelectingVehiclesTempId(tempId);
  };

  const toggleLineVehicle = (tempId: number, vehiculeId: number) => {
    setLignes((current) => current.map((ligne) => {
      if (ligne.tempId !== tempId) return ligne;
      const selected = ligne.vehiculeIds.includes(vehiculeId);
      return {
        ...ligne,
        vehiculeIds: selected
          ? ligne.vehiculeIds.filter((id) => id !== vehiculeId)
          : [...ligne.vehiculeIds, vehiculeId],
        vehicleSelectionHydrated: true,
      };
    }));
  };

  const openPrixEditor = (ligne: LigneItem) => {
    setEditingPrixTempId(ligne.tempId);
    setEditingPrixValue(String(ligne.prixUnitaire));
  };

  const editingPrixNum = parseFloat(editingPrixValue.replace(',', '.')) || 0;

  const confirmPrixEdit = () => {
    if (editingPrixTempId === null || editingPrixNum <= 0) return;
    setLignes(lignes.map((l) => l.tempId === editingPrixTempId ? { ...l, prixUnitaire: editingPrixNum } : l));
    setEditingPrixTempId(null);
    setEditingPrixValue('');
  };

  const totalHT = lignes.reduce((s, l) => s + l.prixUnitaire * l.quantite, 0);
  const tva = selectedClient?.type === 'PARTICULIER' ? 0 : totalHT * 0.2;
  const totalTTC = totalHT + tva;
  const saisieFacture = commandeStatut === 'FACTURE' && selectedClient?.type !== 'PARTICULIER';
  const saisieBonCommande = selectedClient?.type === 'PARTICULIER'
    && (!editCommandeId || commandeStatut === 'BON_COMMANDE');

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!selectedClientId || lignes.length === 0) {
      setSubmitError('Sélectionnez un client et au moins un article.');
      return;
    }

    if (saisieFacture && !numeroFacture.trim()) {
      setSubmitError('Le numéro de facture est obligatoire.');
      return;
    }

    const dureeMois = parseInt(dureeAbonnementMois, 10);
    if (contientAbonnement) {
      const aVehiculeLie = selectedVehiculeId != null || vehiculeIdsConcernes.length > 0;
      if (!aVehiculeLie || isNaN(dureeMois) || dureeMois <= 0) {
        setSubmitError("Les lignes d'abonnement doivent être gérées depuis l'écran Abonnement.");
        return;
      }
    }

    setSaving(true);
    try {
      // Create new (manual) products first, then build the request
      const resolved = await Promise.all(
        lignes.map(async (l) => {
          const ligneData = {
            quantite: l.quantite,
            prixUnitaire: l.prixUnitaire,
            detail: l.detail,
            vehiculeIds: l.vehicleSelectionHydrated ? l.vehiculeIds : undefined,
          };
          if (l.produitId !== null) return { produitServiceId: l.produitId, ...ligneData };
          const created = await produitServiceService.create({ code: l.code, nom: l.nom, prixUnitaire: l.prixUnitaire });
          return { produitServiceId: created.id, ...ligneData };
        })
      );
      const requestPayload = {
        clientId: selectedClientId,
        date,
        lignes: resolved,
        vehiculeId: contientAbonnement ? selectedVehiculeId ?? undefined : undefined,
        vehiculeIds: contientAbonnement && vehiculeIdsConcernes.length > 0 ? vehiculeIdsConcernes : undefined,
        dureeAbonnementMois: contientAbonnement ? dureeMois : undefined,
        numeroFacture: saisieFacture ? numeroFacture.trim() : undefined,
        numeroBonCommande: saisieBonCommande ? numeroBonCommande.trim() || undefined : undefined,
      };

      if (editCommandeId) {
        await dispatch(updateCommande({ id: editCommandeId, data: requestPayload })).unwrap();
      } else {
        await dispatch(createCommande(requestPayload)).unwrap();
      }
      navigation.goBack();
    } catch (e: any) {
      const fallback = editCommandeId
        ? commandeStatut === 'FACTURE'
          ? 'Impossible de modifier la facture.'
          : commandeStatut === 'BON_COMMANDE'
            ? 'Impossible de modifier le bon de commande.'
            : 'Impossible de modifier le devis.'
        : selectedClient?.type === 'PARTICULIER' ? 'Impossible de créer le bon de commande.' : 'Impossible de créer le devis.';
      const message = typeof e === 'string'
        ? e
        : e?.response?.data?.erreur ?? e?.message ?? fallback;
      setSubmitError(typeof message === 'string' ? message : fallback);
    } finally {
      setSaving(false);
    }
  };

  const statutBadge = STATUT_BADGES[commandeStatut ?? (selectedClient?.type === 'PARTICULIER' ? 'BON_COMMANDE' : 'DEVIS')];

  // Erreur + action principale : dans la carte récapitulative sur desktop, sous la carte sur mobile (pleine largeur).
  const ctaBlock = (
    <>
      {submitError ? (
        <View style={styles.submitErrorBox}>
          <MaterialCommunityIcons name="alert-circle-outline" size={18} color={colors.danger} />
          <Text style={styles.submitError}>{submitError}</Text>
        </View>
      ) : null}

      <Button
        mode="contained"
        onPress={handleSubmit}
        loading={saving}
        disabled={saving || !selectedClientId || lignes.length === 0}
        buttonColor={colors.primary}
        textColor={colors.onPrimary}
        style={styles.submitBtn}
        contentStyle={styles.submitBtnContent}
        labelStyle={styles.submitBtnLabel}
        icon={editCommandeId ? 'content-save' : 'check'}
      >
        {saving ? 'Enregistrement...' : editCommandeId ? 'Enregistrer les modifications' : selectedClient?.type === 'PARTICULIER' ? 'Créer le bon de commande' : 'Créer le Devis'}
      </Button>
    </>
  );

  // ── Récapitulatif (colonne de droite sur desktop, fin de page sur mobile) ──
  const summaryCard = (
    <Card raised style={[styles.summaryCard, isDesktop && Platform.OS === 'web' ? STICKY_WEB : null]}>
      <View style={styles.summaryHeader}>
        <Text style={styles.summaryTitle}>Récapitulatif</Text>
        <Badge label={statutBadge.label} tone={statutBadge.tone} dot />
      </View>

      <View style={styles.kvList}>
        <View style={styles.kvRow}>
          <Text style={styles.kvLabel}>Client</Text>
          <Text style={styles.kvValue} numberOfLines={1}>{selectedClient?.nomRaisonSociale ?? '—'}</Text>
        </View>
        <View style={styles.kvRow}>
          <Text style={styles.kvLabel}>Date</Text>
          <Text style={styles.kvValue}>{date || '—'}</Text>
        </View>
        <View style={styles.kvRow}>
          <Text style={styles.kvLabel}>Articles</Text>
          <Text style={styles.kvValue}>{lignes.length}</Text>
        </View>
      </View>

      <View style={styles.summaryDivider} />

      {lignes.length > 0 ? (
        <View style={styles.totaux}>
          {selectedClient?.type !== 'PARTICULIER' && (
            <>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Total HT</Text>
                <Text style={styles.totalValue}>{totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>TVA (20%)</Text>
                <Text style={styles.totalValue}>{tva.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH</Text>
              </View>
            </>
          )}
          <View style={[styles.totalRow, selectedClient?.type !== 'PARTICULIER' && styles.totalTTCRow]}>
            <Text style={styles.totalTTCLabel}>
              {selectedClient?.type === 'PARTICULIER' ? 'Total hors taxe' : 'Total TTC'}
            </Text>
            <Text style={styles.totalTTCValue}>
              {(selectedClient?.type === 'PARTICULIER' ? totalHT : totalTTC).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
            </Text>
          </View>
        </View>
      ) : (
        <Text style={styles.summaryEmpty}>Ajoutez des articles depuis le catalogue pour calculer le total.</Text>
      )}

      {isDesktop ? ctaBlock : null}
    </Card>
  );

  return (
    <Screen>
      <View style={[styles.layout, isDesktop && styles.layoutDesktop]}>
        <View style={[styles.formColumn, isDesktop && styles.formColumnDesktop]}>

          {/* ── Étape 1 : Client ── */}
          <StepCard
            step={1}
            title="Client *"
            subtitle={selectedClient ? undefined : 'Recherchez le client concerné par ce document.'}
          >
            {selectedClient ? (
              <View style={styles.selectedClient}>
                <Avatar name={selectedClient.nomRaisonSociale} size={44} />
                <View style={styles.selectedClientTexts}>
                  <Text style={styles.selectedClientName} numberOfLines={1}>{selectedClient.nomRaisonSociale}</Text>
                  <Badge
                    label={selectedClient.type === 'AGENCE' ? 'Agence' : 'Particulier'}
                    tone={selectedClient.type === 'AGENCE' ? 'brand' : 'neutral'}
                  />
                </View>
                <Pressable onPress={clearSelectedClient} accessibilityRole="button" hitSlop={8} style={styles.changeBtnHit}>
                  <Text style={styles.changeBtn}>Changer</Text>
                </Pressable>
              </View>
            ) : (
              <View>
                <SearchPill
                  placeholder="Rechercher un client..."
                  value={clientSearch}
                  onChangeText={setClientSearch}
                />
                {clientSearch.length > 0 && filteredClients.slice(0, 6).map((c, index) => (
                  <Pressable
                    key={c.id}
                    onPress={() => selectClient(c.id)}
                    accessibilityRole="button"
                    style={(state) => [
                      styles.clientOption,
                      index === 0 && styles.clientOptionFirst,
                      (state as { hovered?: boolean }).hovered && styles.clientOptionHovered,
                    ]}
                  >
                    <Avatar name={c.nomRaisonSociale} size={36} variant="soft" />
                    <Text style={styles.clientOptionName} numberOfLines={1}>{c.nomRaisonSociale}</Text>
                    <Badge
                      label={c.type === 'AGENCE' ? 'Agence' : 'Particulier'}
                      tone={c.type === 'AGENCE' ? 'brand' : 'neutral'}
                    />
                  </Pressable>
                ))}
              </View>
            )}
          </StepCard>

          {/* ── Étape 2 : Document (date, numéros) ── */}
          <StepCard step={2} title="Document" subtitle="Date et références du document.">
            <View style={styles.fieldsRow}>
              <TextInput
                label="Date"
                value={date}
                onChangeText={setDate}
                style={styles.field}
                placeholder="YYYY-MM-DD"
                left={<TextInput.Icon icon="calendar" />}
              />

              {saisieFacture && (
                <TextInput
                  value={numeroFacture}
                  onChangeText={(value) => { setSubmitError(null); setNumeroFacture(value); }}
                  style={styles.field}
                  label="Numéro de facture *"
                  autoCapitalize="characters"
                  maxLength={100}
                />
              )}

              {saisieBonCommande && (
                <TextInput
                  value={numeroBonCommande}
                  onChangeText={(value) => { setSubmitError(null); setNumeroBonCommande(value); }}
                  style={styles.field}
                  label="Numéro du bon de commande"
                  placeholder="Généré automatiquement si vide"
                  autoCapitalize="characters"
                  maxLength={100}
                />
              )}
            </View>
          </StepCard>

          {/* ── Étape 3 : Catalogue produits / services ── */}
          <StepCard
            step={3}
            title="Catalogue produits / services"
            subtitle="Sélectionnez un article pour l'ajouter à la commande."
          >
            {produitsLoading ? (
              <ActivityIndicator color={colors.primary} style={styles.loader} />
            ) : produitsCommandeClassique.length === 0 ? (
              <EmptyState
                icon="package-variant"
                message="Aucun produit hors abonnement disponible."
                hint="Les BAS/AS/A sont gérés depuis l'écran Abonnement."
              />
            ) : (
              <View style={styles.produitsGrid}>
                {produitsCommandeClassique.map((p) => (
                  <View key={p.id} style={[styles.produitCell, isWide ? styles.produitCellThird : styles.produitCellHalf]}>
                    <Pressable
                      onPress={() => addFromCatalog(p)}
                      accessibilityRole="button"
                      accessibilityLabel={`Ajouter ${getServiceDisplayName(p.code, p.nom)}`}
                      style={(state) => [
                        styles.produitBtn,
                        (state as { hovered?: boolean }).hovered && styles.produitBtnHovered,
                        state.pressed && styles.produitBtnPressed,
                      ]}
                    >
                      <View style={styles.produitTop}>
                        <Badge label={p.code.replace('*', '')} tone="neutral" />
                        <View style={styles.produitAddIcon}>
                          <MaterialCommunityIcons name="plus" size={16} color={colors.textPrimary} />
                        </View>
                      </View>
                      <Text style={styles.produitNom} numberOfLines={2}>{getServiceDisplayName(p.code, p.nom)}</Text>
                      <Text style={styles.produitPrix}>{Number(p.prixUnitaire).toLocaleString('fr-FR')} DH</Text>
                    </Pressable>
                  </View>
                ))}
              </View>
            )}

            {/* ── Créer un nouveau service ── */}
            <View style={styles.newServiceBlock}>
              <Button
                mode={showNewServiceForm ? 'text' : 'outlined'}
                icon={showNewServiceForm ? 'close' : 'plus'}
                textColor={showNewServiceForm ? colors.danger : colors.textPrimary}
                onPress={() => { setShowNewServiceForm(!showNewServiceForm); setNsError(null); }}
                style={[styles.newServiceToggle, !showNewServiceForm && styles.outlinedBtn]}
              >
                {showNewServiceForm ? 'Annuler' : 'Créer un nouveau service au catalogue'}
              </Button>

              {showNewServiceForm && (
                <View style={styles.addForm}>
                  <View style={styles.infoNote}>
                    <MaterialCommunityIcons name="information-outline" size={18} color={colors.info} />
                    <Text style={styles.infoNoteText}>
                      Ce service sera ajouté au catalogue et inclus dans cette commande.
                    </Text>
                  </View>
                  <TextInput
                    label="Code * (ex: *GPS)"
                    value={nsCode}
                    onChangeText={(t) => { setNsCode(t.toUpperCase()); setNsError(null); }}
                    autoCapitalize="characters"
                    dense
                  />
                  <TextInput
                    label="Nom du service (optionnel)"
                    value={nsNom}
                    onChangeText={setNsNom}
                    dense
                  />
                  <TextInput
                    label="Prix unitaire (DH) *"
                    value={nsPrix}
                    onChangeText={(t) => { setNsPrix(t); setNsError(null); }}
                    keyboardType="decimal-pad"
                    dense
                    left={<TextInput.Icon icon="currency-usd" />}
                  />
                  {nsError ? <Text style={styles.nsErrorText}>{nsError}</Text> : null}
                  <Button
                    mode="contained"
                    onPress={addNewServiceToCommande}
                    buttonColor={colors.ink}
                    textColor={colors.onInk}
                    style={styles.addFormBtn}
                    icon="plus-circle"
                    disabled={!nsCode.trim() || !nsPrix.trim()}
                  >
                    Ajouter au catalogue et à la commande
                  </Button>
                </View>
              )}
            </View>
          </StepCard>

          {/* ── Étape 4 : Articles sélectionnés ── */}
          {lignes.length > 0 && (
            <StepCard
              step={4}
              title={`Articles sélectionnés (${lignes.length})`}
              subtitle="Ajustez les quantités, les prix et les véhicules concernés."
            >
              {lignes.map((l, index) => {
                const selectedLineVehicules = clientVehicules.filter((vehicule) => l.vehiculeIds.includes(vehicule.id));
                return (
                  <View key={l.tempId} style={[styles.ligneRow, index > 0 && styles.ligneRowDivider]}>
                    <View style={styles.ligneInfo}>
                      <View style={styles.ligneHeader}>
                        <Text style={styles.ligneName} numberOfLines={1}>{getServiceDisplayName(l.code, l.nom)}</Text>
                        <Badge
                          label={l.code.replace('*', '')}
                          tone={l.produitId === null ? 'brand' : 'neutral'}
                        />
                      </View>
                      <View style={styles.lignePrixRow}>
                        <Text style={styles.ligneDetail}>
                          {l.prixUnitaire.toLocaleString('fr-FR')} DH × {l.quantite}
                          {'  =  '}
                          <Text style={styles.ligneSousTotal}>{(l.prixUnitaire * l.quantite).toLocaleString('fr-FR')} DH</Text>
                        </Text>
                        <IconButton
                          icon="pencil-outline"
                          size={16}
                          iconColor={colors.textPrimary}
                          style={styles.editPrixBtn}
                          accessibilityLabel="Modifier le prix unitaire"
                          onPress={() => openPrixEditor(l)}
                        />
                      </View>
                      <View style={styles.lineVehiclesBlock}>
                        <Button
                          compact
                          mode="outlined"
                          icon="car-multiple"
                          textColor={colors.textPrimary}
                          onPress={() => openVehicleSelector(l.tempId)}
                          disabled={!selectedClientId || vehiculesLoading}
                          style={[styles.lineVehiclesButton, styles.outlinedBtn]}
                        >
                          {selectedLineVehicules.length > 0
                            ? `Véhicules (${selectedLineVehicules.length})`
                            : 'Ajouter des véhicules'}
                        </Button>
                        {selectedLineVehicules.length > 0 && (
                          <View style={styles.selectedVehicleBadges}>
                            {selectedLineVehicules.map((vehicule) => (
                              <Badge key={vehicule.id} label={vehicule.immatriculation} icon="car" tone="neutral" />
                            ))}
                          </View>
                        )}
                        {!selectedClientId && (
                          <Text style={styles.vehicleHelp}>Sélectionnez d'abord un client.</Text>
                        )}
                        {selectedClientId && vehiculesError && (
                          <Text style={styles.vehicleError}>{vehiculesError}</Text>
                        )}
                      </View>
                    </View>
                    <View style={styles.ligneQty}>
                      <View style={styles.qtyStepper}>
                        <IconButton
                          icon="minus"
                          size={18}
                          iconColor={colors.textPrimary}
                          style={styles.qtyBtn}
                          accessibilityLabel="Diminuer la quantité"
                          onPress={() => updateQuantite(l.tempId, l.quantite - 1)}
                        />
                        <Text style={styles.qtyText}>{l.quantite}</Text>
                        <IconButton
                          icon="plus"
                          size={18}
                          iconColor={colors.textPrimary}
                          style={styles.qtyBtn}
                          accessibilityLabel="Augmenter la quantité"
                          onPress={() => updateQuantite(l.tempId, l.quantite + 1)}
                        />
                      </View>
                      <IconButton
                        icon="delete-outline"
                        size={20}
                        iconColor={colors.danger}
                        accessibilityLabel="Retirer l'article"
                        onPress={() => removeLigne(l.tempId)}
                      />
                    </View>
                  </View>
                );
              })}
            </StepCard>
          )}
        </View>

        <View style={isDesktop ? [styles.summaryColumn, { width: isWide ? 400 : 360 }] : null}>
          {summaryCard}
          {isDesktop ? null : ctaBlock}
        </View>
      </View>

      {/* Sélection des véhicules pour une ligne de service */}
      <Portal>
        <Modal
          visible={selectingVehiclesTempId !== null}
          onDismiss={() => setSelectingVehiclesTempId(null)}
          style={styles.modalWrapper}
          contentContainerStyle={styles.modal}
        >
          <Text style={styles.modalTitle} numberOfLines={2}>
            Véhicules — {selectingVehicleLine ? getServiceDisplayName(selectingVehicleLine.code, selectingVehicleLine.nom) : ''}
          </Text>
          <SearchPill
            placeholder="Rechercher par immatriculation, marque ou modèle"
            value={vehiculeSearch}
            onChangeText={setVehiculeSearch}
          />
          <ScrollView style={styles.vehicleDialogList} contentContainerStyle={styles.vehicleDialogListContent}>
            {vehiculesLoading ? (
              <ActivityIndicator color={colors.primary} style={styles.loaderLarge} />
            ) : filteredVehicules.length === 0 ? (
              <Text style={styles.emptyText}>
                {clientVehicules.length === 0
                  ? 'Ce client ne possède aucun véhicule.'
                  : 'Aucun véhicule ne correspond à la recherche.'}
              </Text>
            ) : (
              filteredVehicules.map((vehicule) => {
                const checked = selectingVehicleLine?.vehiculeIds.includes(vehicule.id) ?? false;
                return (
                  <Pressable
                    key={vehicule.id}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked }}
                    style={(state) => [
                      styles.vehicleDialogRow,
                      checked && styles.vehicleDialogRowSelected,
                      !checked && (state as { hovered?: boolean }).hovered && styles.vehicleDialogRowHovered,
                    ]}
                    onPress={() => {
                      if (selectingVehiclesTempId !== null) {
                        toggleLineVehicle(selectingVehiclesTempId, vehicule.id);
                      }
                    }}
                  >
                    <Checkbox
                      status={checked ? 'checked' : 'unchecked'}
                      color={colors.ink}
                      uncheckedColor={colors.textSecondary}
                    />
                    <View style={styles.vehicleDialogTexts}>
                      <Text style={styles.vehicleDialogPlate}>{vehicule.immatriculation}</Text>
                      <Text style={styles.vehicleDialogInfo}>{vehicule.marque} {vehicule.modele}</Text>
                    </View>
                  </Pressable>
                );
              })
            )}
          </ScrollView>
          <View style={styles.modalActions}>
            <Button
              mode="contained"
              buttonColor={colors.ink}
              textColor={colors.onInk}
              onPress={() => setSelectingVehiclesTempId(null)}
            >
              Terminer
            </Button>
          </View>
        </Modal>
      </Portal>

      {/* Modification du prix unitaire */}
      <Portal>
        <Modal
          visible={editingPrixTempId !== null}
          onDismiss={() => setEditingPrixTempId(null)}
          style={styles.modalWrapper}
          contentContainerStyle={styles.modal}
        >
          <Text style={styles.modalTitle}>Modifier le prix unitaire</Text>
          <TextInput
            label="Prix unitaire (DH) *"
            value={editingPrixValue}
            onChangeText={setEditingPrixValue}
            keyboardType="decimal-pad"
            autoFocus
            left={<TextInput.Icon icon="currency-usd" />}
          />
          <View style={styles.modalActions}>
            <Button mode="text" textColor={colors.textPrimary} onPress={() => setEditingPrixTempId(null)}>
              Annuler
            </Button>
            <Button mode="contained" onPress={confirmPrixEdit} disabled={editingPrixNum <= 0}>
              Valider
            </Button>
          </View>
        </Modal>
      </Portal>
    </Screen>
  );
}

/** Numbered form step: card + step number + section title. */
function StepCard({
  step,
  title,
  subtitle,
  children,
}: {
  step: number;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  const styles = useStyles();
  return (
    <Card>
      <View style={styles.stepHeader}>
        <View style={styles.stepNumber}>
          <Text style={styles.stepNumberText}>{step}</Text>
        </View>
        <SectionHeader title={title} subtitle={subtitle} style={styles.stepTitle} />
      </View>
      {children}
    </Card>
  );
}

const useStyles = makeStyles((c) => ({
  // Layout
  layout: { gap: 20 },
  layoutDesktop: { flexDirection: 'row', alignItems: 'stretch', gap: 24 },
  formColumn: { gap: 20 },
  formColumnDesktop: { flex: 1, minWidth: 0 },
  summaryColumn: { flexShrink: 0 },
  loader: { marginVertical: 16 },
  loaderLarge: { marginVertical: 24 },
  // Steps
  stepHeader: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: c.surfaceAlt,
    borderWidth: 1,
    borderColor: c.divider,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumberText: { ...TYPOGRAPHY.label, color: c.textPrimary },
  stepTitle: { flex: 1, marginBottom: 0 },
  // Client
  selectedClient: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: RADIUS.md,
    backgroundColor: c.surfaceAlt,
  },
  selectedClientTexts: { flex: 1, minWidth: 0, gap: 6 },
  selectedClientName: { ...TYPOGRAPHY.bodyStrong, color: c.textPrimary },
  changeBtnHit: { paddingVertical: 8, paddingHorizontal: 4 },
  changeBtn: { ...TYPOGRAPHY.label, fontSize: 14, color: c.textPrimary, textDecorationLine: 'underline' },
  clientOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: RADIUS.md,
  },
  clientOptionFirst: { marginTop: 12 },
  clientOptionHovered: { backgroundColor: c.surfaceAlt },
  clientOptionName: { flex: 1, minWidth: 0, fontSize: 15, fontWeight: '500', color: c.textPrimary },
  // Document fields
  fieldsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  field: { flexGrow: 1, flexBasis: 220 },
  // Empty states
  emptyText: { fontSize: 14, color: c.textSecondary, fontWeight: '500', textAlign: 'center', paddingVertical: 24 },
  // Products grid
  produitsGrid: { flexDirection: 'row', flexWrap: 'wrap', margin: -6 },
  produitCell: { padding: 6 },
  produitCellHalf: { width: '50%' },
  produitCellThird: { width: '33.333%' },
  produitBtn: {
    flexGrow: 1,
    backgroundColor: c.surface,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: c.divider,
    padding: 14,
    gap: 6,
  },
  produitBtnHovered: { borderColor: c.ink, backgroundColor: c.surfaceAlt },
  produitBtnPressed: { transform: [{ scale: 0.98 }] },
  produitTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  produitAddIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: c.border,
    backgroundColor: c.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  produitNom: { fontSize: 14, lineHeight: 19, color: c.textPrimary, fontWeight: '500' },
  produitPrix: { fontSize: 15, fontWeight: '700', color: c.textPrimary },
  // New service form
  newServiceBlock: { marginTop: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: c.divider },
  newServiceToggle: { alignSelf: 'flex-start' },
  outlinedBtn: { borderColor: c.border },
  addForm: {
    marginTop: 12,
    padding: 16,
    gap: 12,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: c.divider,
    backgroundColor: c.surfaceAlt,
  },
  infoNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: c.infoSoft,
    borderRadius: RADIUS.sm,
    padding: 10,
  },
  infoNoteText: { flex: 1, fontSize: 13, lineHeight: 18, color: c.info },
  nsErrorText: { fontSize: 13, color: c.danger, fontWeight: '500' },
  addFormBtn: { alignSelf: 'flex-start', marginTop: 4 },
  // Lines
  ligneRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', gap: 12, paddingVertical: 16 },
  ligneRowDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  ligneInfo: { flexGrow: 1, flexShrink: 1, flexBasis: 240, minWidth: 0 },
  ligneHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  ligneName: { flexShrink: 1, ...TYPOGRAPHY.bodyStrong, color: c.textPrimary },
  lignePrixRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' },
  ligneDetail: { fontSize: 14, color: c.textSecondary },
  ligneSousTotal: { fontWeight: '700', color: c.textPrimary },
  editPrixBtn: { margin: 0, marginLeft: 2 },
  ligneQty: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  qtyStepper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: c.border,
    borderRadius: RADIUS.pill,
    paddingHorizontal: 2,
  },
  qtyBtn: { margin: 0 },
  qtyText: { fontSize: 15, fontWeight: '700', minWidth: 24, textAlign: 'center', color: c.textPrimary },
  lineVehiclesBlock: { marginTop: 10, alignItems: 'flex-start' },
  lineVehiclesButton: { borderRadius: RADIUS.pill },
  selectedVehicleBadges: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  vehicleHelp: { color: c.textMuted, fontSize: 12, marginTop: 6 },
  vehicleError: { color: c.danger, fontSize: 12, marginTop: 6 },
  // Modals
  modalWrapper: { padding: 16 },
  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    maxWidth: 560,
    width: '100%',
    alignSelf: 'center',
    gap: 16,
  },
  modalTitle: { ...TYPOGRAPHY.title, color: c.textPrimary },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8 },
  vehicleDialogList: { maxHeight: 360 },
  vehicleDialogListContent: { gap: 2 },
  vehicleDialogRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingRight: 12,
    borderRadius: RADIUS.md,
  },
  vehicleDialogRowSelected: { backgroundColor: c.surfaceAlt },
  vehicleDialogRowHovered: { backgroundColor: c.surfaceAlt },
  vehicleDialogTexts: { flex: 1, minWidth: 0 },
  vehicleDialogPlate: { color: c.textPrimary, fontSize: 15, fontWeight: '600' },
  vehicleDialogInfo: { color: c.textSecondary, fontSize: 13, marginTop: 1 },
  // Summary
  summaryCard: { padding: 24 },
  summaryHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 },
  summaryTitle: { ...TYPOGRAPHY.title, color: c.textPrimary, flexShrink: 1 },
  kvList: { gap: 10 },
  kvRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  kvLabel: { fontSize: 14, color: c.textSecondary },
  kvValue: { flexShrink: 1, textAlign: 'right', fontSize: 15, fontWeight: '600', color: c.textPrimary },
  summaryDivider: { height: 1, backgroundColor: c.divider, marginVertical: 16 },
  summaryEmpty: { fontSize: 14, lineHeight: 20, color: c.textSecondary },
  totaux: { gap: 8 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  totalLabel: { fontSize: 14, color: c.textSecondary },
  totalValue: { fontSize: 15, color: c.textPrimary, fontWeight: '500' },
  totalTTCRow: { marginTop: 8, paddingTop: 16, borderTopWidth: 1, borderTopColor: c.divider },
  totalTTCLabel: { ...TYPOGRAPHY.subheading, fontWeight: '700', color: c.textPrimary },
  totalTTCValue: { ...TYPOGRAPHY.heading, color: c.textPrimary },
  // Submit
  submitErrorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 16,
    padding: 12,
    borderRadius: RADIUS.md,
    backgroundColor: c.dangerSoft,
  },
  submitError: { flex: 1, fontSize: 13, lineHeight: 18, color: c.danger, fontWeight: '600' },
  submitBtn: { marginTop: 20, borderRadius: RADIUS.md },
  submitBtnContent: { paddingVertical: 8 },
  submitBtnLabel: { fontWeight: '700' },
}));
